import { useEffect, useState } from 'react';
import { Button, Flex, IconButton, Tab, TabList, TabPanel, TabPanels, Tabs, Tooltip } from '@chakra-ui/react';
import { MdDelete, MdEdit } from 'react-icons/md';

import { BreakdownEquipmentType, DepartmentRegion } from '@app/api/models/equipment';
import { MultiSelectField, SelectField, Txt } from '@app/components/common';
import Dialog from '@app/components/common/modals/dialog';
import { TableColumn } from '@app/components/common/table';
import { TableBase } from '@app/components/common/table/table-base';
import { useAlert, useApi } from '@app/hooks';

const COUNTRYWIDE_REGION = 'на всю страну';

const toId = (item?: EntityBase) => (
  item?.id === undefined || item?.id === '' ? undefined : Number(item.id)
);

const toMap = (items: EntityBase[]) =>
  new Map(items.map((item) => [Number(item.id), item.name || '']));

type BreakdownRow = EntityBase & {
  pid: number;
  breakdown_id: number;
};

type DepartmentRow = EntityBase & {
  pid: number;
  department_id: number;
  region_id?: number | null;
  region_name?: string;
};

type EditEquipmentTypeProps = {
  open: boolean;
  equipmentType?: EntityBase;
  equipmentTypes: EntityBase[];
  onClose: () => void;
};

type BreakdownFormProps = {
  open: boolean;
  equipmentTypeId?: number;
  equipmentTypes: EntityBase[];
  breakdownGuides: EntityBase[];
  breakdownLinks: BreakdownEquipmentType[];
  link?: BreakdownRow;
  onClose: () => void;
  onSaved: () => Promise<void>;
};

type DepartmentFormProps = {
  open: boolean;
  equipmentTypeId?: number;
  departments: EntityBase[];
  regions: EntityBase[];
  departmentLinks: DepartmentRegion[];
  link?: DepartmentRow;
  onClose: () => void;
  onSaved: () => Promise<void>;
};

const BreakdownForm = ({
  open,
  equipmentTypeId,
  equipmentTypes,
  breakdownGuides,
  breakdownLinks,
  link,
  onClose,
  onSaved,
}: BreakdownFormProps) => {
  const api = useApi();
  const { error, success, warning } = useAlert();
  const isEdit = !!link;

  const [loading, setLoading] = useState(false);
  const [selectedBreakdowns, setSelectedBreakdowns] = useState<EntityBase[]>([]);
  const [breakdown, setBreakdown] = useState<EntityBase>();
  const [copyFrom, setCopyFrom] = useState<EntityBase>();

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedBreakdowns([]);
    setCopyFrom(undefined);
    setBreakdown(
      link
        ? { id: link.breakdown_id, name: link.name }
        : undefined
    );
  }, [open, link]);

  const activeBreakdowns = breakdownLinks.filter((item) => item.is_active);

  const onCopyBreakdowns = async (source?: EntityBase) => {
    setCopyFrom(source);
    const sourceId = toId(source);
    if (!sourceId) {
      return;
    }
    setLoading(true);
    try {
      const response = await api.equipments.getBreakdownEquipmentTypes({
        limit: 5000,
        offset: 0,
        sort: '+pid',
        filter: { equipment_type_id: sourceId, is_active: true },
      });
      const activeIds = new Set((response?.data?.data || []).map((item) => item.breakdown_id));
      const currentIds = new Set(activeBreakdowns.map((item) => item.breakdown_id));
      setSelectedBreakdowns(
        breakdownGuides.filter((item) => activeIds.has(Number(item.id)) && !currentIds.has(Number(item.id)))
      );
    } catch (e) {
      error(e.message || 'Не удалось скопировать набор поломок');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    if (!equipmentTypeId) {
      return;
    }

    if (isEdit && link) {
      const breakdownId = toId(breakdown);
      if (!breakdownId) {
        warning('Выберите поломку');
        return;
      }
      const duplicate = activeBreakdowns.find(
        (item) => item.breakdown_id === breakdownId && item.pid !== link.pid
      );
      if (duplicate) {
        warning('Эта поломка уже привязана к типу оборудования.');
        return;
      }
      if (breakdownId === link.breakdown_id) {
        onClose();
        return;
      }

      setLoading(true);
      try {
        const updated = await api.equipments.updateBreakdownEquipmentType({
          pid: link.pid,
          equipment_type_id: equipmentTypeId,
          breakdown_id: breakdownId,
        });
        if (updated?.code !== 200) {
          return;
        }
        success('Поломка сохранена');
        onClose();
        await onSaved();
      } catch (e) {
        error(e.message || 'Не удалось сохранить поломку');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!selectedBreakdowns.length) {
      warning('Выберите хотя бы одну поломку');
      return;
    }
    const activeIds = new Set(activeBreakdowns.map((item) => item.breakdown_id));
    const duplicated = selectedBreakdowns.filter((item) => activeIds.has(Number(item.id)));
    if (duplicated.length) {
      warning(`Эти поломки уже привязаны: ${duplicated.map((item) => item.name).join(', ')}.`);
      return;
    }

    setLoading(true);
    try {
      for (const item of selectedBreakdowns) {
        const breakdownId = Number(item.id);
        const inactive = breakdownLinks.find((current) => !current.is_active && current.breakdown_id === breakdownId);
        if (inactive) {
          const activated = await api.equipments.deleteBreakdownEquipmentType({
            pid: inactive.pid,
            is_active: true,
          });
          if (activated?.code !== 200) {
            await onSaved();
            return;
          }
          continue;
        }
        const created = await api.equipments.createBreakdownEquipmentType({
          equipment_type_id: equipmentTypeId,
          breakdown_id: breakdownId,
        });
        if (created?.code !== 200 && created?.code !== 201) {
          await onSaved();
          return;
        }
      }
      success(selectedBreakdowns.length > 1 ? `Добавлено поломок: ${selectedBreakdowns.length}` : 'Поломка добавлена');
      onClose();
      await onSaved();
    } catch (e) {
      error(e.message || 'Не удалось добавить поломки');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      loading={loading}
      title={isEdit ? 'Изменение поломки' : 'Новые поломки'}
      width={560}
      actions={
        <Flex justifyContent="space-between" w="100%">
          <Button colorScheme="brand" onClick={onSubmit} isLoading={loading}>
            {isEdit ? 'Сохранить' : 'Добавить'}
          </Button>
          <Button onClick={onClose}>Отмена</Button>
        </Flex>
      }
    >
      <Flex direction="column" gap={3}>
        {isEdit ? (
          <SelectField
            id="breakdown"
            label="Поломка"
            required
            clearable={false}
            options={breakdownGuides}
            value={breakdown}
            onChange={setBreakdown}
          />
        ) : (
          <>
            <SelectField
              id="copy_breakdowns"
              label="Скопировать поломки с типа"
              options={equipmentTypes.filter((item) => Number(item.id) !== equipmentTypeId)}
              value={copyFrom}
              onChange={onCopyBreakdowns}
            />
            <MultiSelectField
              id="breakdowns"
              label="Поломки"
              options={breakdownGuides}
              value={selectedBreakdowns}
              onChange={setSelectedBreakdowns}
            />
            <Txt color="gray.500" fontSize="sm">
              Можно выбрать сразу несколько поломок или подставить набор с другого типа оборудования.
            </Txt>
          </>
        )}
      </Flex>
    </Dialog>
  );
};

const DepartmentForm = ({
  open,
  equipmentTypeId,
  departments,
  regions,
  departmentLinks,
  link,
  onClose,
  onSaved,
}: DepartmentFormProps) => {
  const api = useApi();
  const { error, success, warning } = useAlert();
  const isEdit = !!link;

  const [loading, setLoading] = useState(false);
  const [department, setDepartment] = useState<EntityBase>();
  const [region, setRegion] = useState<EntityBase>();

  useEffect(() => {
    if (!open) {
      return;
    }
    setDepartment(
      link
        ? { id: link.department_id, name: link.name }
        : undefined
    );
    const regionName = regions.find((item) => Number(item.id) === Number(link?.region_id))?.name;
    setRegion(
      link?.region_id
        ? {
          id: link.region_id,
          name: regionName || link.region_name || `Регион #${link.region_id}`,
        }
        : undefined
    );
  }, [open, link, regions]);

  const onSubmit = async () => {
    if (!equipmentTypeId) {
      return;
    }
    const departmentId = toId(department);
    if (!departmentId) {
      warning('Выберите сервисную организацию');
      return;
    }
    const regionId = toId(region) ?? null;
    const duplicate = departmentLinks.find((item) => (
      item.is_active
      && item.pid !== link?.pid
      && item.department_id === departmentId
      && (item.region_id ?? null) === regionId
    ));
    if (duplicate) {
      warning('Такая связь «сервисная организация + регион» для этого типа оборудования уже есть.');
      return;
    }

    setLoading(true);
    try {
      if (isEdit && link) {
        const regionChanged = (link.region_id ?? null) !== regionId;
        if (regionChanged) {
          const removed = await api.equipments.deleteDepartmentRegion({
            pid: link.pid,
            is_active: false,
          });
          if (removed?.code !== 200) {
            return;
          }
          const created = await api.equipments.createDepartmentRegion({
            department_id: departmentId,
            equipment_type_id: equipmentTypeId,
            ...(regionId ? { region_id: regionId } : {}),
          });
          if (created?.code !== 200 && created?.code !== 201) {
            return;
          }
        } else {
          const updated = await api.equipments.updateDepartmentRegion({
            pid: link.pid,
            department_id: departmentId,
            equipment_type_id: equipmentTypeId,
          });
          if (updated?.code !== 200) {
            return;
          }
        }
        success('Связь сохранена');
      } else {
        const inactive = departmentLinks.find((item) => (
          !item.is_active
          && item.department_id === departmentId
          && (item.region_id ?? null) === regionId
        ));
        if (inactive) {
          const activated = await api.equipments.deleteDepartmentRegion({
            pid: inactive.pid,
            is_active: true,
          });
          if (activated?.code !== 200) {
            return;
          }
        } else {
          const created = await api.equipments.createDepartmentRegion({
            department_id: departmentId,
            equipment_type_id: equipmentTypeId,
            ...(regionId ? { region_id: regionId } : {}),
          });
          if (created?.code !== 200 && created?.code !== 201) {
            return;
          }
        }
        success('Сервисная организация добавлена');
      }
      onClose();
      await onSaved();
    } catch (e) {
      error(e.message || 'Не удалось сохранить сервисную организацию');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      loading={loading}
      title={isEdit ? 'Изменение сервисной организации' : 'Новая сервисная организация'}
      width={560}
      actions={
        <Flex justifyContent="space-between" w="100%">
          <Button colorScheme="brand" onClick={onSubmit} isLoading={loading}>
            {isEdit ? 'Сохранить' : 'Добавить'}
          </Button>
          <Button onClick={onClose}>Отмена</Button>
        </Flex>
      }
    >
      <Flex direction="column" gap={3}>
        <SelectField
          id="service_department"
          label="Сервисная организация"
          required
          clearable={false}
          options={departments}
          value={department}
          onChange={setDepartment}
        />
        <SelectField
          id="region"
          label="Регион"
          options={regions}
          value={region}
          onChange={setRegion}
        />
        <Txt color="gray.500" fontSize="sm">
          Если регион не выбран, организация действует на всю страну.
        </Txt>
      </Flex>
    </Dialog>
  );
};

export const EditEquipmentType = ({
  open,
  equipmentType,
  equipmentTypes,
  onClose,
}: EditEquipmentTypeProps) => {
  const api = useApi();
  const { error, success } = useAlert();

  const [loading, setLoading] = useState(false);
  const [breakdownGuides, setBreakdownGuides] = useState<EntityBase[]>([]);
  const [departments, setDepartments] = useState<EntityBase[]>([]);
  const [regions, setRegions] = useState<EntityBase[]>([]);
  const [breakdownLinks, setBreakdownLinks] = useState<BreakdownEquipmentType[]>([]);
  const [departmentLinks, setDepartmentLinks] = useState<DepartmentRegion[]>([]);
  const [breakdownForm, setBreakdownForm] = useState<BreakdownRow | null>();
  const [departmentForm, setDepartmentForm] = useState<DepartmentRow | null>();

  const equipmentTypeId = toId(equipmentType);
  const breakdownFormOpen = breakdownForm !== undefined;
  const departmentFormOpen = departmentForm !== undefined;

  const load = async (typeId: number) => {
    setLoading(true);
    try {
      const [breakdownsRes, departmentsRes, regionsRes, breakdownLinksRes, departmentLinksRes] = await Promise.all([
        api.guides.get({ name: 'service_breakdown' }),
        api.guides.get({ name: 'service_department' }),
        api.guides.get({ name: 'region' }),
        api.equipments.getBreakdownEquipmentTypes({
          limit: 5000,
          offset: 0,
          sort: '+pid',
          filter: { equipment_type_id: typeId },
        }),
        api.equipments.getDepartmentRegions({
          limit: 5000,
          offset: 0,
          sort: '+pid',
          filter: { equipment_type_id: typeId },
        }),
      ]);
      setBreakdownGuides(breakdownsRes?.data || []);
      setDepartments(departmentsRes?.data || []);
      setRegions(
        [...(regionsRes?.data || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'))
      );
      setBreakdownLinks(breakdownLinksRes?.data?.data || []);
      setDepartmentLinks(departmentLinksRes?.data?.data || []);
    } catch (e) {
      error(e.message || 'Не удалось загрузить связи типа оборудования');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !equipmentTypeId) {
      return;
    }
    setBreakdownForm(undefined);
    setDepartmentForm(undefined);
    load(equipmentTypeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, equipmentTypeId]);

  const breakdownNames = toMap(breakdownGuides);
  const departmentNames = toMap(departments);
  const regionNames = toMap(regions);
  const activeBreakdowns = breakdownLinks.filter((item) => item.is_active);
  const activeDepartments = departmentLinks.filter((item) => item.is_active);

  const breakdownRows: BreakdownRow[] = activeBreakdowns.map((item) => ({
    id: item.pid,
    pid: item.pid,
    breakdown_id: item.breakdown_id,
    name: breakdownNames.get(item.breakdown_id) || `[${item.breakdown_id}]`,
  }));

  const departmentRows: DepartmentRow[] = activeDepartments.map((item) => ({
    id: item.pid,
    pid: item.pid,
    department_id: item.department_id,
    name: departmentNames.get(item.department_id) || `[${item.department_id}]`,
    region_id: item.region_id,
    region_name: item.region_id
      ? regionNames.get(Number(item.region_id)) || `Регион #${item.region_id}`
      : COUNTRYWIDE_REGION,
  }));

  const onSaved = async () => {
    if (equipmentTypeId) {
      await load(equipmentTypeId);
    }
  };

  const onRemoveBreakdown = async (pid: number) => {
    if (!equipmentTypeId) {
      return;
    }
    setLoading(true);
    try {
      const response = await api.equipments.deleteBreakdownEquipmentType({
        pid,
        is_active: false,
      });
      if (response?.code === 200) {
        success('Поломка отвязана');
        await load(equipmentTypeId);
      } else {
        setLoading(false);
      }
    } catch (e) {
      error(e.message || 'Не удалось отвязать поломку');
      setLoading(false);
    }
  };

  const onRemoveDepartment = async (pid: number) => {
    if (!equipmentTypeId) {
      return;
    }
    setLoading(true);
    try {
      const response = await api.equipments.deleteDepartmentRegion({
        pid,
        is_active: false,
      });
      if (response?.code === 200) {
        success('Сервисная организация отвязана');
        await load(equipmentTypeId);
      } else {
        setLoading(false);
      }
    } catch (e) {
      error(e.message || 'Не удалось отвязать сервисную организацию');
      setLoading(false);
    }
  };

  const breakdownColumns: TableColumn<BreakdownRow>[] = [
    {
      name: 'name',
      label: 'Поломка',
      options: { filter: false, sort: false },
    },
    {
      name: 'pid',
      label: '',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (pid: number, { rowData }) => (
          <Flex gap={2} justifyContent="flex-end">
            <Tooltip label="Изменить">
              <IconButton aria-label="edit" size="sm" onClick={() => setBreakdownForm(rowData)}>
                <MdEdit />
              </IconButton>
            </Tooltip>
            <Tooltip label="Удалить">
              <IconButton aria-label="remove" size="sm" onClick={() => onRemoveBreakdown(pid)}>
                <MdDelete />
              </IconButton>
            </Tooltip>
          </Flex>
        ),
      },
    },
  ];

  const departmentColumns: TableColumn<DepartmentRow>[] = [
    {
      name: 'name',
      label: 'Сервисная организация',
      options: { filter: false, sort: false },
    },
    {
      name: 'region_name',
      label: 'Регион',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: string | undefined, { rowData }) => (
          value || (rowData.region_id ? `Регион #${rowData.region_id}` : COUNTRYWIDE_REGION)
        ),
      },
    },
    {
      name: 'pid',
      label: '',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (pid: number, { rowData }) => (
          <Flex gap={2} justifyContent="flex-end">
            <Tooltip label="Изменить">
              <IconButton aria-label="edit" size="sm" onClick={() => setDepartmentForm(rowData)}>
                <MdEdit />
              </IconButton>
            </Tooltip>
            <Tooltip label="Удалить">
              <IconButton aria-label="remove" size="sm" onClick={() => onRemoveDepartment(pid)}>
                <MdDelete />
              </IconButton>
            </Tooltip>
          </Flex>
        ),
      },
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        loading={loading}
        title={equipmentType?.name || 'Тип оборудования'}
        width={760}
        noscroll
        actions={<Button onClick={onClose}>Закрыть</Button>}
      >
        <Tabs key={equipmentTypeId} isLazy>
          <TabList>
            <Tab>Поломки</Tab>
            <Tab>Сервисные организации</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <TableBase<BreakdownRow>
                title=""
                paper={false}
                keyProperty="pid"
                columns={breakdownColumns}
                data={breakdownRows}
                onCreateClick={() => setBreakdownForm(null)}
              />
            </TabPanel>
            <TabPanel px={0}>
              <TableBase<DepartmentRow>
                title=""
                paper={false}
                keyProperty="pid"
                columns={departmentColumns}
                data={departmentRows}
                onCreateClick={() => setDepartmentForm(null)}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Dialog>
      <BreakdownForm
        open={breakdownFormOpen}
        equipmentTypeId={equipmentTypeId}
        equipmentTypes={equipmentTypes}
        breakdownGuides={breakdownGuides}
        breakdownLinks={breakdownLinks}
        link={breakdownForm || undefined}
        onClose={() => setBreakdownForm(undefined)}
        onSaved={onSaved}
      />
      <DepartmentForm
        open={departmentFormOpen}
        equipmentTypeId={equipmentTypeId}
        departments={departments}
        regions={regions}
        departmentLinks={departmentLinks}
        link={departmentForm || undefined}
        onClose={() => setDepartmentForm(undefined)}
        onSaved={onSaved}
      />
    </>
  );
};
